import PhrasePill from './PhrasePill';
import addPhraseToClause from '../actions/helpers/addPhraseToClause';
import removePhraseFromClause from '../actions/helpers/removePhraseFromClause';
import setClauseErrorText from '../actions/helpers/setClauseErrorText';
import setIncludesWordsInputText from '../actions/helpers/setIncludesWordsInputText';
import store from '../store/store';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import KeyboardCharCodes from 'owa-hotkeys/lib/utils/keyboardCharCodes';
import * as React from 'react';

const styles = require('./IncludesWordsInput.scss');
let classNames = require('classnames/bind').bind(styles);

class IncludesWordsInputComputedState {
    constructor(private props: IncludesWordsInputProps) {}

    @computed
    get isFocused(): boolean {
        return store.inboxRulesEditState.focusedClauseId == this.props.clauseId;
    }
}

export interface IncludesWordsInputProps {
    clauseId: string;
    placeHolderText: string;
    onBlur: () => void;
    onFocus: () => void;
}

@observer
export default class IncludesWordsInput extends React.Component<IncludesWordsInputProps, {}> {
    private inputRef: HTMLInputElement;
    private computedState: IncludesWordsInputComputedState;

    constructor(props: IncludesWordsInputProps) {
        super(props);
        this.computedState = new IncludesWordsInputComputedState(this.props);
    }

    public focus() {
        this.inputRef.focus();
    }

    render() {
        let clauseId = this.props.clauseId;
        let selectedClause = store.inboxRulesEditState.clauseMap.get(clauseId);
        let errorText = store.inboxRulesEditState.clauseErrorTextMap.get(clauseId);
        let committedPhrases = selectedClause.clauseValue as string[];
        let inputContainerClassName = classNames(
            styles.includesWordsInputContainer,
            this.computedState.isFocused &&
                this.inputRef == document.activeElement &&
                styles.inputFocused,
            errorText && styles.hasError
        );

        return (
            <div className={styles.includesWordsContainer}>
                <div className={inputContainerClassName}>
                    {this.renderCommittedPhrases(committedPhrases)}
                    <input
                        ref={ref => (this.inputRef = ref)}
                        onFocus={this.props.onFocus}
                        onBlur={this.onBlur}
                        className={styles.phraseInput}
                        value={store.inboxRulesEditState.includesWordsInputTextMap.get(clauseId)}
                        placeholder={committedPhrases.length > 0 ? '' : this.props.placeHolderText}
                        onChange={this.onInputChange}
                        onKeyDown={this.onKeyDown}
                    />
                </div>
                {errorText && <span className={styles.invalidInput}>{errorText}</span>}
            </div>
        );
    }

    private onInputChange = () => {
        setIncludesWordsInputText(this.props.clauseId, this.inputRef.value);
    };

    private renderCommittedPhrases(committedPhrases: string[]) {
        let phrasePills = [];
        if (committedPhrases.length > 0) {
            committedPhrases.forEach(phrase => {
                phrasePills.push(
                    <PhrasePill key={phrase} clauseId={this.props.clauseId} phrase={phrase} />
                );
            });
        }
        return phrasePills;
    }

    private onKeyDown = (evt: any) => {
        let clauseId = this.props.clauseId;
        let inputText = store.inboxRulesEditState.includesWordsInputTextMap.get(clauseId);
        switch (evt.keyCode) {
            case KeyboardCharCodes.Enter:
                evt.stopPropagation();
                if (this.isValidInput(inputText)) {
                    // clear the error text in case the user is creating their first pill while the error state is shown
                    setClauseErrorText(clauseId, true /*isValidClause*/);
                    addPhraseToClause(clauseId, inputText, 'Keyboard');
                }
                break;
            case KeyboardCharCodes.Backspace:
                evt.stopPropagation();
                if (!inputText) {
                    this.removeLastPhraseFromClause(clauseId, inputText);
                }
                break;
        }
    };

    private onBlur = () => {
        let inputText = store.inboxRulesEditState.includesWordsInputTextMap.get(
            this.props.clauseId
        );
        if (this.isValidInput(inputText)) {
            addPhraseToClause(this.props.clauseId, inputText, 'Blur');
        }
        this.props.onBlur();
    };

    private isValidInput(inputText: string): boolean {
        // if user presses enter with empty input or only whitespace, input is invalid
        return inputText && inputText.trim().length > 0;
    }

    private removeLastPhraseFromClause(clauseId: string, phrase: string) {
        let existingPhrases = store.inboxRulesEditState.clauseMap.get(clauseId)
            .clauseValue as string[];

        // if there are no phrases to delete (if a user presses backspace on an empty input), do nothing
        if (!existingPhrases || existingPhrases.length == 0) {
            return;
        }

        removePhraseFromClause(this.props.clauseId, existingPhrases[existingPhrases.length - 1]);
    }
}
